import { AccountInfoResult, AccountModel } from '@block-explorer/entities';
import {
  useGetAccountsPublicKey,
  usePostAccountsWithInfo,
} from './event-store-hooks';
import { indexBy, uniq } from 'remeda';
import {
  isValidAccountHash,
  isValidPublicKey,
  isValidURef,
} from '../utils/account';
import { useCallback } from 'react';

interface GetAccountInfo {
  loading: boolean;
  getAccountInfo: <T>(publicKey: string) => T | undefined;
  getAccountInfoByHash: (accountHash: string) => AccountInfoResult | undefined;
  getPublicKeyAccountHash: (accountHash: string) => string | undefined;
  getPublicKeyByURef: (uref: string) => string | undefined;
}

const KEY_SUFFIX_REG_EXP = /-\d{3}$/;
const normalizeAccountKey = (key: string) =>
  key.replace(KEY_SUFFIX_REG_EXP, '');

const resolveAccountHash = (key: string): string | null => {
  if (key == null) {
    return null;
  }

  const normalizedKey = normalizeAccountKey(key);

  if (isValidPublicKey(normalizedKey)) {
    return AccountModel({
      publicKeyHex: normalizedKey,
    }).getAccountHash();
  }

  return isValidAccountHash(normalizedKey) ? normalizedKey : null;
};

export const useCreateAccountsInfoGetter = (
  publicKeyList: string[] | null,
  accountHashList: string[] | null = []
): GetAccountInfo => {
  const purseURefs = uniq(
    [...(publicKeyList || []), ...(accountHashList || [])].filter(isValidURef)
  );

  const { data: purseAccounts, loading: loadingPurseAccounts } =
    useGetAccountsPublicKey(
      purseURefs.length
        ? {
            main_purse_uref: purseURefs.join(','),
            page_size: purseURefs.length,
          }
        : null
    );

  const validAccountHashList = accountHashList?.filter(isValidAccountHash);

  const knownAccountHashList =
    publicKeyList
      ?.map(resolveAccountHash)
      .filter((hash): hash is string => hash !== null) ?? null;

  const purseAccountHashes = (purseAccounts || [])
    .map((account) => account.account_hash)
    .filter(isValidAccountHash);

  const accountHashes = uniq([
    ...(knownAccountHashList || []),
    ...(validAccountHashList || []),
    ...purseAccountHashes,
  ]);

  const { data: accounts, loading: loadingAccounts } = usePostAccountsWithInfo(
    accountHashes.length === 0
      ? null
      : {
          account_hashes: accountHashes,
          includes:
            'account_info,centralized_account_info,cspr_name,auction_status',
          order_by: 'balance',
          page_size: accountHashes.length,
        }
  );

  const accountsInfoByHash = indexBy(accounts || [], (i) => i.account_hash);

  const getAccountInfo = useCallback(
    (publicKey: string): any | undefined => {
      const key = resolveAccountHash(publicKey);
      const accountInfoByHash = key ? accountsInfoByHash[key] : undefined;

      return accountInfoByHash;
    },
    [accountsInfoByHash]
  );

  const getAccountInfoByHash = useCallback(
    (accountHash: string): AccountInfoResult | undefined => {
      const accountInfoByHash =
        isValidAccountHash(accountHash) && accountsInfoByHash[accountHash];

      return accountInfoByHash ? accountInfoByHash : undefined;
    },
    [accountsInfoByHash]
  );

  const getPublicKeyAccountHash = useCallback(
    (accountHash: string): string | undefined => {
      const publicKeyByHash =
        isValidAccountHash(accountHash) &&
        accountsInfoByHash[accountHash]?.public_key;

      return publicKeyByHash ? publicKeyByHash : undefined;
    },
    [accountsInfoByHash]
  );

  const getPublicKeyByURef = useCallback(
    (uref: string): string | undefined => {
      const accountWithMainPurseUref = [
        ...(accounts || []),
        ...(purseAccounts || []),
      ].find(
        (account) =>
          account.main_purse_uref?.toLowerCase() === uref.toLowerCase()
      );

      return accountWithMainPurseUref?.public_key;
    },
    [accounts, purseAccounts]
  );

  return {
    getAccountInfo,
    getAccountInfoByHash,
    getPublicKeyAccountHash,
    getPublicKeyByURef,
    loading: loadingAccounts || loadingPurseAccounts,
  };
};
